const REF_STORAGE_KEY = 'ref_params';
const UTM_STORAGE_KEY = 'utm_params';
const REF_EXPIRY_DAYS = 30; 

interface RefParams {
  ref?: string;
  timestamp?: number;
}

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  timestamp?: number;
}

export const extractUTMParams = (): RefParams => {
  const params = new URLSearchParams(window.location.search);
  const refParams: RefParams = {};

  const refValue = params.get('ref');
  if (refValue) {
    refParams.ref = refValue;
    refParams.timestamp = Date.now();
  }

  return refParams;
};

/**
 * Extract all UTM parameters from current URL
 * Used for analytics and detailed tracking
 */
export const extractFullUTMParams = (): UTMParams => {
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmTerm = params.get('utm_term');
  const utmContent = params.get('utm_content');

  if (utmSource) utmParams.utm_source = utmSource;
  if (utmMedium) utmParams.utm_medium = utmMedium;
  if (utmCampaign) utmParams.utm_campaign = utmCampaign;
  if (utmTerm) utmParams.utm_term = utmTerm;
  if (utmContent) utmParams.utm_content = utmContent;

  if (Object.keys(utmParams).length > 0) {
    utmParams.timestamp = Date.now();
  }

  return utmParams;
};

export const saveUTMParams = (params: RefParams): void => {
  if (!params.ref) {
    return;
  }

  try {
    localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to save ref parameter:', error);
  }
};

export const saveFullUTMParams = (params: UTMParams): void => {
  if (Object.keys(params).length === 0) {
    return;
  }

  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to save UTM parameters:', error);
  }
};

export const getStoredUTMParams = (): RefParams | null => {
  try {
    const stored = localStorage.getItem(REF_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const params: RefParams = JSON.parse(stored);
    
    if (params.timestamp) {
      const daysSince = (Date.now() - params.timestamp) / (1000 * 60 * 60 * 24);
      if (daysSince > REF_EXPIRY_DAYS) {
        localStorage.removeItem(REF_STORAGE_KEY);
        return null;
      }
  
    }

    return params;
  } catch (error) {
    console.warn('Failed to retrieve ref parameter:', error);
    return null;
  }
};

export const clearUTMParams = (): void => {
  try {
    localStorage.removeItem(REF_STORAGE_KEY);
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear parameters:', error);
  }
};

export const initializeUTMTracking = (): void => {
  // Extract and save ref parameter for database
  const urlParams = extractUTMParams();
  if (urlParams.ref) {
    saveUTMParams(urlParams);
  } else {
    getStoredUTMParams();
  }
  
  const utmParams = extractFullUTMParams();
  if (Object.keys(utmParams).length > 0) {
    saveFullUTMParams(utmParams);
  }
  
};

/**
 * Get the ref value to save in ref_signup
 * Priority: ref in URL > stored ref > utm_source in URL > stored utm_source > null
 * 
 * This allows using either 'ref' parameter or falling back to 'utm_source' if ref is not present
 */
export const getRefSignupValue = (): string | null => {
  
  // First priority: ref parameter
  const urlParams = extractUTMParams();
  if (urlParams.ref) {
    return urlParams.ref;
  }

  // Second priority: stored ref
  const storedParams = getStoredUTMParams();
  if (storedParams?.ref) {
    return storedParams.ref;
  }

  // Third priority: utm_source from URL
  const utmParams = extractFullUTMParams();
  if (utmParams.utm_source) {
    return utmParams.utm_source;
  }

  // Fourth priority: stored utm_source
  try {
    const storedUTM = localStorage.getItem(UTM_STORAGE_KEY);
    if (storedUTM) {
      const storedUTMParams: UTMParams = JSON.parse(storedUTM);
      if (storedUTMParams.utm_source) {
        return storedUTMParams.utm_source;
      }
    }
  } catch (error) {
    console.warn('Failed to get UTM parameters:', error);
  }

  return null;
};
